import { useState, useEffect, useCallback } from 'react';
import { ExternalLink, Check, Loader2, X } from 'lucide-react';
import { coworkApi } from '../api/coworkApi';

// `available: false` channels aren't wired to a connect flow yet — they show a
// muted "Soon" pill instead of an actionable Connect button, so a CMO doesn't
// click into a dead end.
const SUPPORTED_CHANNELS = [
  { provider: 'google-ads',  label: 'Google Ads',         category: 'Paid',     icon: '🔵', viewKey: 'google-ads' as const, available: true },
  { provider: 'meta-ads',    label: 'Meta Ads',           category: 'Paid',     icon: '🔷', available: true },
  { provider: 'tiktok-ads',  label: 'TikTok Ads',         category: 'Paid',     icon: '🎵', available: false },
  { provider: 'linkedin',    label: 'LinkedIn',           category: 'Social',   icon: '💼', available: true },
  { provider: 'instagram',   label: 'Instagram',          category: 'Social',   icon: '📸', available: false },
  { provider: 'twitter',     label: 'X / Twitter',        category: 'Social',   icon: '🐦', available: false },
  { provider: 'tiktok',      label: 'TikTok',             category: 'Social',   icon: '🎬', available: false },
  { provider: 'ga4',         label: 'Google Analytics 4', category: 'Analytics', icon: '📊', available: true },
  { provider: 'klaviyo',     label: 'Klaviyo',            category: 'Email/CRM', icon: '✉️', available: false },
  { provider: 'hubspot',     label: 'HubSpot',            category: 'Email/CRM', icon: '🟠', available: false },
  { provider: 'gmail',       label: 'Gmail',              category: 'Email/CRM', icon: '✉️', available: false },
  { provider: 'slack',       label: 'Slack',              category: 'Comms',     icon: '💬', available: true },
];

interface Integration {
  id: string;
  provider: string;
  profileName?: string | null;
  profileImage?: string | null;
  createdAt?: string;
}

interface IntegrationsPageProps {
  onOpenGoogleAds: () => void;
}

export function IntegrationsPage({ onOpenGoogleAds }: IntegrationsPageProps) {
  const [items, setItems] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [metaStatus, setMetaStatus] = useState<{ connected: boolean; profileName?: string | null; adAccountId?: string | null; adAccountName?: string | null } | null>(null);
  const [showMetaPicker, setShowMetaPicker] = useState(false);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setLoading(true);
    coworkApi.integrations.list()
      .then(d => setItems(d.integrations || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
    coworkApi.integrations.metaAdsStatus().then(setMetaStatus).catch(() => setMetaStatus({ connected: false }));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Re-check after returning from any OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const consumed: string[] = [];

    if (params.get('meta_connected') === 'true') {
      refresh();
      setShowMetaPicker(true);
      consumed.push('meta_connected', 'brandId');
    } else if (params.get('meta_error')) {
      alert(`Meta connection failed: ${params.get('meta_error')}`);
      consumed.push('meta_error');
    }
    if (params.get('gads_connected') === 'true' || params.get('integration_success') || params.get('slack_connected') === 'true') {
      refresh();
      ['gads_connected', 'integration_success', 'slack_connected', 'brandId', 'profile'].forEach(k => consumed.push(k));
    }
    if (params.get('integration_error') || params.get('gads_error') || params.get('slack_error')) {
      alert(`Integration failed: ${params.get('integration_error') || params.get('gads_error') || params.get('slack_error')}`);
      ['integration_error', 'gads_error', 'slack_error'].forEach(k => consumed.push(k));
    }

    if (consumed.length) {
      const url = new URL(window.location.href);
      consumed.forEach(k => url.searchParams.delete(k));
      window.history.replaceState({}, '', url.toString());
    }
  }, [refresh]);

  const handleConnect = (provider: string) => {
    if (provider === 'meta-ads') {
      window.location.href = coworkApi.integrations.metaAdsConnectUrl();
    } else if (provider === 'google-ads') {
      window.location.href = coworkApi.integrations.googleAdsConnectUrl();
    } else if (provider === 'linkedin') {
      window.location.href = coworkApi.integrations.connectUrl();
    } else if (provider === 'slack') {
      window.location.href = coworkApi.slack.connectUrl();
    } else if (provider === 'ga4') {
      window.location.href = coworkApi.ga4.connectUrl();
    } else {
      alert(`Connection flow for ${provider} coming soon.`);
    }
  };

  const handleDisconnect = async (integration: Integration) => {
    if (!confirm(`Disconnect ${integration.profileName || integration.provider}? Your agents will lose access to this account.`)) return;
    setDisconnecting(integration.id);
    try {
      await coworkApi.integrations.delete(integration.id);
      setItems(prev => prev.filter(i => i.id !== integration.id));
    } catch (err) {
      alert(`Failed to disconnect: ${(err as Error).message}`);
    } finally {
      setDisconnecting(null);
    }
  };

  const grouped = SUPPORTED_CHANNELS.reduce<Record<string, typeof SUPPORTED_CHANNELS>>((acc, c) => {
    (acc[c.category] ||= []).push(c);
    return acc;
  }, {});

  const byProvider = items.reduce<Record<string, Integration>>((acc, i) => {
    acc[i.provider] = i;
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="px-6 py-4 border-b border-[#eaecf0]">
        <h1 className="text-[18px] font-semibold text-[#101828]">Channels & integrations</h1>
        <p className="text-[12px] text-[#667085] mt-0.5">Connect the platforms your agents work across.</p>
      </div>

      <div className="flex-1 overflow-auto px-6 py-6">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-[#94a3b8]" /></div>
        ) : (
          <div className="flex flex-col gap-6 max-w-4xl">
            {Object.entries(grouped).map(([category, channels]) => (
              <div key={category}>
                <div className="text-[11px] uppercase tracking-wider text-[#94a3b8] mb-2 font-semibold">{category}</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {channels.map((c) => {
                    const integration = byProvider[c.provider];
                    const isConnected = !!integration;
                    const isMeta = c.provider === 'meta-ads';

                    let subtitle: string;
                    if (isMeta && metaStatus?.connected) {
                      subtitle = metaStatus.adAccountName
                        ? `${metaStatus.profileName || 'Connected'} · ${metaStatus.adAccountName}`
                        : `${metaStatus.profileName || 'Connected'} · pick an ad account`;
                    } else if (isConnected) {
                      subtitle = integration.profileName || 'Connected';
                    } else {
                      subtitle = 'Not connected';
                    }

                    return (
                      <div key={c.provider} className="flex items-center gap-3 p-3 rounded-lg border border-[#eaecf0] hover:border-[#be43ff]/30 transition-colors">
                        {integration?.profileImage ? (
                          <img src={integration.profileImage} alt="" className="w-8 h-8 rounded-md object-cover" />
                        ) : (
                          <div className="text-[20px] w-8 h-8 flex items-center justify-center bg-[#f8fafc] rounded-md">{c.icon}</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold text-[#101828]">{c.label}</div>
                          <div className="text-[11px] text-[#94a3b8] truncate">{subtitle}</div>
                        </div>

                        {isConnected ? (
                          <span className="flex items-center gap-1 text-[11px] text-[#16a34a] px-2.5 py-1 rounded-md bg-[#f0fdf4]">
                            <Check className="w-3 h-3" />
                            Connected
                          </span>
                        ) : c.available ? (
                          <button
                            onClick={() => handleConnect(c.provider)}
                            className="text-[11px] text-[#be43ff] px-2.5 py-1 rounded-md hover:bg-[#f8f5ff] transition-colors"
                            style={{ fontWeight: 600 }}
                          >
                            Connect
                          </button>
                        ) : (
                          <span className="text-[11px] text-[#94a3b8] px-2.5 py-1 rounded-md bg-[#f8fafc]" style={{ fontWeight: 500 }}>
                            Soon
                          </span>
                        )}

                        {isMeta && isConnected && (
                          <button
                            onClick={() => setShowMetaPicker(true)}
                            className="text-[11px] text-[#667085] hover:text-[#101828] px-2 py-1 rounded-md hover:bg-[#f8fafc]"
                            title="Choose ad account"
                          >
                            {metaStatus?.adAccountId ? 'Change account' : 'Pick account'}
                          </button>
                        )}

                        {c.viewKey === 'google-ads' && isConnected && (
                          <button
                            onClick={onOpenGoogleAds}
                            className="p-1.5 rounded hover:bg-[#f8f5ff] text-[#be43ff]"
                            title="Open Google Ads agent"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </button>
                        )}

                        {isConnected && integration && (
                          <button
                            onClick={() => handleDisconnect(integration)}
                            disabled={disconnecting === integration.id}
                            className="text-[11px] text-[#94a3b8] hover:text-[#dc2626] px-2 py-1 rounded-md hover:bg-[#fef2f2] transition-colors disabled:opacity-50"
                            title="Disconnect"
                          >
                            {disconnecting === integration.id ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Disconnect'}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showMetaPicker && (
        <MetaAdAccountPicker
          currentAccountId={metaStatus?.adAccountId || null}
          onClose={() => setShowMetaPicker(false)}
          onSelected={() => {
            setShowMetaPicker(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

interface MetaAdAccount {
  adAccountId: string;
  accountId: string;
  name: string;
  status: string;
  currency: string;
  businessName?: string;
}

function MetaAdAccountPicker({ currentAccountId, onClose, onSelected }: {
  currentAccountId: string | null;
  onClose: () => void;
  onSelected: () => void;
}) {
  const [accounts, setAccounts] = useState<MetaAdAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    coworkApi.integrations.metaAdsAccounts()
      .then(d => setAccounts(d.accounts))
      .catch(err => setError(err.message || 'Failed to load ad accounts'));
  }, []);

  const pick = async (a: MetaAdAccount) => {
    setSaving(a.adAccountId);
    try {
      await coworkApi.integrations.metaAdsSelectAccount(a.adAccountId, a.name);
      onSelected();
    } catch (err) {
      setError((err as Error).message || 'Failed to save selection');
      setSaving(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#eaecf0]">
          <div>
            <div className="text-[14px] font-semibold text-[#101828]">Choose Meta ad account</div>
            <div className="text-[11px] text-[#94a3b8]">Agents will run campaigns on the account you pick.</div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f8fafc]"><X className="w-4 h-4 text-[#94a3b8]" /></button>
        </div>

        <div className="flex-1 overflow-auto p-3">
          {error && (
            <div className="text-[12px] text-[#dc2626] bg-[#fef2f2] rounded-md p-3 mb-2">{error}</div>
          )}
          {!accounts && !error && (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#94a3b8]" /></div>
          )}
          {accounts && accounts.length === 0 && (
            <div className="text-[12px] text-[#94a3b8] text-center py-10">
              No ad accounts found on your Meta Business profile. Create one in Meta Business Manager and reconnect.
            </div>
          )}
          {accounts && accounts.map(a => {
            const isCurrent = a.adAccountId === currentAccountId;
            const isSaving = saving === a.adAccountId;
            return (
              <button
                key={a.adAccountId}
                onClick={() => pick(a)}
                disabled={isSaving}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors mb-1 ${isCurrent ? 'border-[#be43ff] bg-[#f8f5ff]' : 'border-[#eaecf0] hover:border-[#be43ff]/30 hover:bg-[#f8f5ff]/50'}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-[#101828] truncate">{a.name}</div>
                  <div className="text-[11px] text-[#94a3b8] truncate">
                    {a.businessName ? `${a.businessName} · ` : ''}
                    {a.adAccountId} · {a.currency} · {a.status}
                  </div>
                </div>
                {isCurrent && <Check className="w-4 h-4 text-[#be43ff]" />}
                {isSaving && <Loader2 className="w-4 h-4 animate-spin text-[#94a3b8]" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
